/**
 * Domain prospek. Tidak mengimpor domain lain (ADR-043).
 *
 * Seluruh rute di sini menerima JWT (`require_dashboard_or_service`), jadi bisa
 * dipanggil browser tanpa perubahan backend apa pun.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, GalatApi, kueri } from '../../shared/api/client'

export const kunci = {
  daftar: (saring) => ['prospek', 'daftar', saring],
  satu: (id) => ['prospek', id],
  percakapan: (id) => ['prospek', id, 'percakapan'],
  aktivitas: (id) => ['prospek', id, 'aktivitas'],
}

export function useProspek(saring = {}) {
  return useQuery({
    queryKey: kunci.daftar(saring),
    queryFn: () => api.get(`/dashboard/prospects${kueri(saring)}`),
  })
}

export function useProspekSatu(id) {
  return useQuery({
    queryKey: kunci.satu(id),
    queryFn: () => api.get(`/dashboard/prospects/${id}`),
    enabled: !!id,
  })
}

export function usePercakapan(id) {
  return useQuery({
    queryKey: kunci.percakapan(id),
    queryFn: () => api.get(`/dashboard/prospects/${id}/conversation`),
    enabled: !!id,
  })
}

export function useAktivitas(id) {
  return useQuery({
    queryKey: kunci.aktivitas(id),
    queryFn: () => api.get(`/dashboard/prospects/${id}/activity`),
    enabled: !!id,
  })
}

/**
 * Prospek manual — dari luar Instagram: pameran, telepon, rujukan.
 *
 * Rutenya (`POST /dashboard/prospects`) sudah ada sejak repo lama, tapi tombol "Tambah
 * manual" di daftar prospek tidak pernah memanggilnya — tombolnya tanpa `onClick` sama
 * sekali, jadi ditekan pun tidak terjadi apa-apa (laporan operator 22 September).
 *
 * Daftar dimuat ulang, dan hasilnya langsung ditaruh di tembolok detail supaya halaman
 * yang dituju sesudah menyimpan tidak berkedip memuat.
 */
export function useBuatProspek() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body) => api.post('/dashboard/prospects', body),
    onSuccess: (data) => {
      if (data?.id) qc.setQueryData(kunci.satu(data.id), data)
      qc.invalidateQueries({ queryKey: ['prospek', 'daftar'] })
    },
  })
}

/**
 * Sunting prospek — termasuk saklar `ai_paused` (pegang / lepas percakapan).
 *
 * Nilai lama TIDAK hilang: `ProspectService.update` menulis `audit_log` berisi
 * nilai sebelum dan sesudah. Jadi "siapa PIC-nya bulan lalu" tetap terjawab
 * sesudah ditimpa — itu sebabnya layar boleh menimpa tanpa tabel riwayat sendiri.
 */
/**
 * Catatan proses — apa yang terjadi di luar sistem: ditelepon, kirim proposal, ditolak.
 *
 * Ada rutenya sejak lama (`POST /dashboard/prospects/{id}/process-events`) dan tidak ada
 * satu pun tombol yang memanggilnya, jadi kronologi prospek cuma berisi yang otomatis.
 * Rancangan menaruh tombolnya di bawah panel Catatan proses.
 */
export function useTambahCatatan(id) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body) => api.post(`/dashboard/prospects/${id}/process-events`, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: kunci.aktivitas(id) })
      qc.invalidateQueries({ queryKey: kunci.satu(id) })
    },
  })
}

/** Ubah satu catatan proses: judulnya, dan rinciannya kalau ada. */
export function useUbahCatatan(id) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ catatanId, ...isi }) => api.patch(`/dashboard/process-events/${catatanId}`, isi),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: kunci.aktivitas(id) })
      qc.invalidateQueries({ queryKey: kunci.satu(id) })
    },
  })
}

/** Hapus satu catatan proses. Permanen; jejaknya tinggal di `audit_log`. */
export function useHapusCatatan(id) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ catatanId }) => api.del(`/dashboard/process-events/${catatanId}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: kunci.aktivitas(id) })
      qc.invalidateQueries({ queryKey: kunci.satu(id) })
    },
  })
}

/**
 * Yang ikut terhapus kalau orang di balik prospek ini dihapus (ADR-125). Tenant Admin ke
 * atas. Diminta saat pertanyaannya dibuka (`aktif`), dan selalu segar.
 */
export function usePratinjauHapusProspek(id, { aktif = false } = {}) {
  return useQuery({
    queryKey: [...kunci.satu(id), 'hapus'],
    queryFn: () => api.get(`/dashboard/prospects/${id}/hapus`),
    enabled: !!id && aktif,
    staleTime: 0,
    gcTime: 0,
  })
}

/**
 * Hapus orang di balik prospek ini permanen, beserta percakapannya (ADR-125).
 *
 * Semua kueri diminta ulang (daftar prospek, kotak masuk, lencana menu), KECUALI milik
 * prospek ini sendiri: detail yang sedang terbuka akan dijawab 404, dan layarnya berpindah
 * ke daftar begitu ini selesai.
 */
export function useHapusProspek(id) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: () => api.del(`/dashboard/prospects/${id}`),
    onSuccess: () => qc.invalidateQueries({
      predicate: (q) => !(q.queryKey[0] === 'prospek' && q.queryKey[1] === id),
    }),
  })
}

/**
 * Jadwalkan meeting untuk prospek ini.
 *
 * Memuat ulang PROSPEKNYA, bukan cuma daftar meeting: `ProspectOut.meetings` yang
 * dipakai tab Meeting, jadi menyegarkan daftar lain saja membuat meeting barunya tidak
 * muncul sampai halaman dimuat ulang.
 */
export function useBuatMeeting(id) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body) => api.post(`/dashboard/prospects/${id}/meetings`, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: kunci.satu(id) })
      qc.invalidateQueries({ queryKey: kunci.aktivitas(id) })
      qc.invalidateQueries({ queryKey: ['meeting', 'saya'] })
      qc.invalidateQueries({ queryKey: ['meeting', 'tanggal'] })
      qc.invalidateQueries({ queryKey: ['meeting', 'ketersediaan'] })
      qc.invalidateQueries({ queryKey: ['meeting', 'tenant'] })
      qc.invalidateQueries({ queryKey: ['prospek', 'daftar'] })
      // Satu meeting bisa membuat harinya penuh: "Yang ditawarkan bot sekarang" di P2 (ADR-124).
      qc.invalidateQueries({ queryKey: ['ta', 'meeting-pratinjau'] })
    },
  })
}

export function useUbahProspek(id) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (perubahan) => api.patch(`/dashboard/prospects/${id}`, perubahan),
    onSuccess: (data) => {
      qc.setQueryData(kunci.satu(id), data)
      qc.invalidateQueries({ queryKey: ['prospek', 'daftar'] })
      // Saklar bot mengubah siapa yang memegang percakapannya (Perlu dijawab).
      if ('ai_paused' in data) qc.invalidateQueries({ queryKey: ['percakapan'] })
    },
  })
}

/**
 * Terima atau tolak seorang calon di gerbang persetujuan (ADR-101): `{ keputusan: 'terima' }`,
 * atau `{ keputusan: 'tolak', alasan, catatan }`. Jawabannya prospeknya, sebentuk dengan GET.
 */
export function usePutuskanProspek(id) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (isi) => api.post(`/dashboard/prospects/${id}/keputusan`, isi),
    onSuccess: ({ tawaran: _hasilTawaran, ...prospek }) => {
      // `tawaran` hasil pengiriman tawaran meetingnya (ADR-103): milik jawaban ini, bukan
      // bagian prospeknya. Tawaran yang terkirim menambah satu pesan di utas percakapannya.
      qc.setQueryData(kunci.satu(id), prospek)
      qc.invalidateQueries({ queryKey: ['prospek', 'daftar'] })
      qc.invalidateQueries({ queryKey: kunci.percakapan(id) })
      qc.invalidateQueries({ queryKey: ['percakapan'] })
    },
  })
}

/**
 * Siapa yang bisa dicentang sebagai peserta meeting.
 *
 * BUKAN `/dashboard/roster` — rute itu menuntut `tenant_admin`, dan yang memilih peserta
 * adalah staf marketing. Dipakai bersama di seluruh kartu meeting sebuah prospek, jadi
 * kuncinya tidak memuat id prospek: satu ambilan, bukan satu per kartu yang terbuka.
 */
export function useTim() {
  return useQuery({ queryKey: ['tim'], queryFn: () => api.get('/dashboard/tim') })
}

/**
 * Peserta sebuah meeting — daftar centangnya dikirim UTUH, bukan selisihnya.
 *
 * Backend menimpa semua peserta yang PUNYA kotak centang dengan yang dikirim, jadi mengirim
 * satu orang berarti membuang yang lain. Peserta tanpa kotak (tanpa akun Smova, atau akunnya
 * sudah nonaktif) tetap peserta, kecuali disebut di `keluarkan` (ADR-107 no. 21): tiap
 * isinya `web_user_id` orangnya, atau namanya kalau ia tidak punya akun.
 */
export function useUbahPeserta(profileId) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ meetingId, ids, keluarkan = [] }) =>
      api.patch(`/dashboard/meetings/${meetingId}/assignees`,
        { assignee_ids: ids, ...(keluarkan.length ? { keluarkan } : {}) }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: kunci.satu(profileId) })
      qc.invalidateQueries({ queryKey: ['meeting', 'saya'] })
      qc.invalidateQueries({ queryKey: ['meeting', 'tenant'] })
    },
  })
}

/** Jadwal, bentuk, tempat, dan agenda satu meeting. */
export function useUbahJadwal(profileId) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ meetingId, ...isi }) =>
      api.patch(`/dashboard/meetings/${meetingId}/schedule`, isi),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: kunci.satu(profileId) })
      qc.invalidateQueries({ queryKey: kunci.aktivitas(profileId) })
      qc.invalidateQueries({ queryKey: ['meeting', 'tanggal'] })
      qc.invalidateQueries({ queryKey: ['meeting', 'ketersediaan'] })
      qc.invalidateQueries({ queryKey: ['meeting', 'saya'] })
      qc.invalidateQueries({ queryKey: ['meeting', 'tenant'] })
      qc.invalidateQueries({ queryKey: ['prospek', 'daftar'] })
      qc.invalidateQueries({ queryKey: ['ta', 'meeting-pratinjau'] })
    },
  })
}

/** Hasil pertemuan dan catatannya. Tidak memberi tahu siapa pun — lihat OutcomeRequest. */
export function useUbahHasil(profileId) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ meetingId, ...isi }) =>
      api.patch(`/dashboard/meetings/${meetingId}/outcome`, isi),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: kunci.satu(profileId) })
      qc.invalidateQueries({ queryKey: ['prospek', 'daftar'] })
      qc.invalidateQueries({ queryKey: ['meeting', 'saya'] })
      qc.invalidateQueries({ queryKey: ['meeting', 'tenant'] })
      // Meeting yang dibatalkan membuka lagi harinya.
      qc.invalidateQueries({ queryKey: ['ta', 'meeting-pratinjau'] })
    },
  })
}

/**
 * BATAS UKURAN AUDIO — diturunkan dari batas nginx, bukan dipilih.
 *
 * `client_max_body_size 8M` di vhost dashboard membatasi seluruh badan permintaan, dan
 * audio dikirim sebagai base64 di dalam JSON — yang MEMBENGKAKKANNYA sepertiga
 * (4 bait untuk tiap 3 bait asli), ditambah prefiks data-URL dan pembungkus JSON-nya.
 * Jadi berkas 6 MB berangkat sebagai badan ~8,1 MB dan ditolak nginx.
 *
 * Yang ditolak nginx TIDAK terbaca siapa pun: ia menjawab halaman HTML "413 Request
 * Entity Too Large" yang tidak menyebut batas apa pun, dan permintaannya tidak pernah
 * sampai ke aplikasi — jadi tidak ada satu baris pun di log worker. Itu persis yang
 * dilaporkan operator 13 September.
 *
 * SEJAK 14 September proxy-nya dinaikkan ke 36 MB PADA RUTE INI SAJA — 25 MB ditambah
 * sepertiga base64-nya — jadi yang menolak sekarang aplikasinya, dengan kalimat yang bisa
 * dibaca. Angka di bawah karena itu sama dengan batas aplikasi, bukan lebih kecil: penjaga
 * yang lebih ketat daripada servernya menolak berkas yang sebenarnya diterima.
 *
 * KALAU 413 MASIH MUNCUL, yang salah bukan berkasnya melainkan proxy-nya — dan
 * `jelaskanRekaman` memang mengatakan itu, bukan menyuruh mengecilkan berkas lagi.
 */
export const BATAS_AUDIO = 25_000_000

/**
 * Unggah satu rekaman rapat. DITAMBAHKAN ke yang sudah ada, tidak menimpa.
 *
 * Audionya dikirim base64 di JSON, bukan multipart — rutenya memang dibentuk begitu
 * supaya gateway Sentris dan n8n cukup meneruskan JSON apa adanya.
 */
export function useUnggahRekaman(profileId) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ meetingId, berkas }) => {
      // DITOLAK DI SINI, bukan di proxy. Yang ditolak proxy kembali sebagai HTML tanpa
      // sebab; yang ditolak di sini bisa menyebut ukuran berkasnya sendiri, batasnya,
      // dan jalan lain yang tersedia.
      if (berkas.size > BATAS_AUDIO) {
        throw new GalatApi('audio_terlalu_besar', {
          status: 0, kode: 'audio_terlalu_besar',
          mentah: { ukuran: berkas.size, batas: BATAS_AUDIO },
        })
      }
      const b64 = await new Promise((selesai, gagal) => {
        const p = new FileReader()
        p.onerror = () => gagal(new Error('Berkas tidak terbaca'))
        // `result` berbentuk `data:audio/…;base64,XXXX` — rutenya menerima keduanya,
        // jadi prefiksnya tidak dibuang di sini.
        p.onload = () => selesai(String(p.result))
        p.readAsDataURL(berkas)
      })
      return api.post(`/meetings/${meetingId}/recording`, {
        audio_base64: b64, filename: berkas.name,
      })
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: kunci.satu(profileId) }),
  })
}

/**
 * Buang satu rekaman dari sebuah meeting, beserta transkripnya.
 *
 * Sampai 14 September tidak ada jalan sama sekali untuk membuangnya — dan sejak rekaman
 * bisa DITAMBAHKAN, satu berkas yang salah unggah menempel permanen di catatan resmi
 * pertemuan.
 *
 * Nomor yang dikirim adalah nomor yang sedang DITAMPILKAN. Backend menomori ulang
 * sesudah menghapus, jadi keduanya selalu sepakat tanpa layar perlu menyimpan id apa pun.
 */
export function useHapusRekaman(profileId) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ meetingId, nomor }) =>
      api.del(`/meetings/${meetingId}/recording/${nomor}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: kunci.satu(profileId) }),
  })
}
